"use client";

/**
 * StatTileGrid — the live-figure tiles used by the coverage, quality, fleet and safety strips.
 *
 * WHY THIS EXISTS. The same tile was written out four times, in index-coverage-live,
 * index-quality-live, node-fleet-live and safety-stats-live, and had drifted in spelling and in
 * spacing between the copies. One copy is now the only copy.
 *
 * (Three of them said `tabular-num` and one `tabular-nums`, which looks like a typo and is not:
 * `.tabular-num` is a house shorthand defined in app/globals.css and it ships in the built CSS,
 * so both spellings render identically. Checked before assuming.)
 *
 * WHAT WAS WRONG WITH IT, measured on the live page rather than guessed at:
 *
 *   coverage   186px tiles, 151px inside the padding. "Continuous" renders 154px at the old size,
 *              so it overflowed its own tile by 3px.
 *   machines   120px tiles, 86px inside. EVERY label wrapped to two lines and every tile stood
 *              204px tall — a column of narrow slivers rather than a row of cards.
 *
 * Both come from the same root cause: the value slot is styled for a short numeral — big, heavy,
 * `leading-none` — and then asked to hold "Continuous", "Shared card throughput" or six tiles in
 * the width of four.
 *
 * SO THE SIZE IS CHOSEN PER GRID, NOT PER TILE. The longest value in the set decides the size for
 * all of them. Sizing each tile on its own would be the obvious fix and it looks wrong: "5.7M"
 * would sit large beside a smaller "6.3 TB" in the same row, which reads as a mistake rather than
 * as emphasis. A row of figures has to share a size to be comparable at a glance, which is the
 * whole point of putting them in a row.
 */

import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type StatTileItem = {
  icon: LucideIcon;
  value: string;
  label: string;
  sub?: string;
};

/* Steps down as the longest value grows. The thresholds are where the text stops fitting the
   narrowest tile the grid can produce, not round numbers picked by eye: a 4-character value clears
   the 86px fleet tile, and "Continuous" at 10 characters needs the smallest step to clear 151px. */
function valueSize(items: StatTileItem[]): string {
  const longest = items.reduce((n, s) => Math.max(n, String(s.value ?? "").length), 0);
  if (longest <= 4) return "text-2xl md:text-3xl";
  if (longest <= 7) return "text-xl md:text-2xl";
  return "text-lg md:text-xl";
}

export function StatTileGrid({
  items,
  className,
}: {
  items: StatTileItem[];
  /** Extra classes for the grid. Column counts are NOT set here — see `.stat-tile-grid`. */
  className?: string;
}) {
  const size = valueSize(items);

  /* `.stat-tile-grid` decides the columns from the CONTAINER's width rather than the window's,
     which is the only thing that can be right for a strip used in four different column widths.
     Callers deliberately do not pass grid-cols any more: a caller that hardcodes six columns has
     no idea whether it is sitting in 733px or 1100px, and that is exactly how this broke. */
  return (
    <div className={cn("stat-tile-grid gap-3", className)}>
      {items.map((s) => (
        /* `min-w-0` because a grid item's default `min-width:auto` refuses to shrink below its
           content, which is how one long word widens a whole column instead of wrapping in it. */
        <div key={s.label} className="lift-card min-w-0 rounded-xl border hairline bg-card p-4">
          <s.icon className="mb-3 h-4 w-4 opacity-50" aria-hidden />
          {/* `leading-none` is right for a numeral and wrong for anything that might wrap, so the
              line height relaxes just enough to keep two lines legible if it ever comes to that.
              `break-words` is the backstop: a value longer than anything we ship today wraps
              inside the tile rather than spilling out of it. */}
          <div className={cn("font-semibold tabular-nums leading-[1.05] break-words", size)}>
            {s.value}
          </div>
          {/* TIGHTER THAN IT WAS. `mt-2` under a numeral that already carries its own leading put
              roughly a line of air between a figure and the words naming it, so the two read as
              separate items rather than one fact. Same correction as the read-rate strip. */}
          <div className="mt-1.5 text-sm leading-snug opacity-80">{s.label}</div>
          {s.sub ? <div className="mt-0.5 text-xs leading-snug opacity-50">{s.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}
